import { Button } from '@shared/components/ui/button';
import { DynamicCombobox } from '@shared/components/ui/DynamicCombobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { cn } from '@shared/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMinimumFeeFilterOptions } from '../../hooks/useMinimumFeeFilterOptions';
import { MinimumFeesFilterState } from '../../hooks/useMinimumFeesMain';

interface MinimumFeesFiltersPanelProps {
  filters: MinimumFeesFilterState;
  onChange: (nextFilters: Partial<MinimumFeesFilterState>) => void;
  onClear: () => void;
  className?: string;
}

const ALL_VALUE = 'all';

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

export const MinimumFeesFiltersPanel = ({ filters, onChange, onClear, className }: MinimumFeesFiltersPanelProps) => {
  const { t } = useTranslation('minimumFeesModule');
  const { searchTeams, resolveTeamOption, searchInvoices, resolveInvoiceOption } = useMinimumFeeFilterOptions();

  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(
    () =>
      Array.from({ length: 8 }, (_, index) => String(currentYear - 5 + index)).map((year) => ({
        label: year,
        value: year,
      })),
    [currentYear],
  );

  const monthOptions = useMemo(
    () => [
      { label: t('months.1', 'Enero'), value: '1' },
      { label: t('months.2', 'Febrero'), value: '2' },
      { label: t('months.3', 'Marzo'), value: '3' },
      { label: t('months.4', 'Abril'), value: '4' },
      { label: t('months.5', 'Mayo'), value: '5' },
      { label: t('months.6', 'Junio'), value: '6' },
      { label: t('months.7', 'Julio'), value: '7' },
      { label: t('months.8', 'Agosto'), value: '8' },
      { label: t('months.9', 'Septiembre'), value: '9' },
      { label: t('months.10', 'Octubre'), value: '10' },
      { label: t('months.11', 'Noviembre'), value: '11' },
      { label: t('months.12', 'Diciembre'), value: '12' },
    ],
    [t],
  );

  const activeFiltersCount = useMemo(() => Object.values(filters).filter(Boolean).length, [filters]);

  return (
    <div className={cn('space-y-5', className)}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FilterField label={t('filters.client', 'Cliente')}>
          <DynamicCombobox
            value={filters.team_id}
            setValue={(value) => onChange({ team_id: value })}
            placeholder={t('filters.clientPlaceholder', 'Seleccionar cliente')}
            searchFn={searchTeams}
            resolveSelectedOption={resolveTeamOption}
            dataTestId="filter-team"
            emptyMessage={t('filters.noClients', 'No se encontraron clientes')}
          />
        </FilterField>

        <FilterField label={t('filters.year', 'Año')}>
          <Select
            value={filters.year || ALL_VALUE}
            onValueChange={(value) => onChange({ year: value === ALL_VALUE ? '' : value })}
          >
            <SelectTrigger className="h-[38px] text-sm" data-testid="filter-year">
              <SelectValue placeholder={t('filters.allYears', 'Todos')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>{t('filters.allYears', 'Todos')}</SelectItem>
              {yearOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label={t('filters.month', 'Mes')}>
          <Select
            value={filters.month || ALL_VALUE}
            onValueChange={(value) => onChange({ month: value === ALL_VALUE ? '' : value })}
          >
            <SelectTrigger className="h-[38px] text-sm" data-testid="filter-month">
              <SelectValue placeholder={t('filters.allMonths', 'Todos')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>{t('filters.allMonths', 'Todos')}</SelectItem>
              {monthOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label={t('filters.status', 'Estado')}>
          <Select
            value={filters.status || ALL_VALUE}
            onValueChange={(value) => onChange({ status: value === ALL_VALUE ? '' : value })}
          >
            <SelectTrigger className="h-[38px] text-sm" data-testid="filter-status">
              <SelectValue placeholder={t('filters.allStatuses', 'Todos')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>{t('filters.allStatuses', 'Todos')}</SelectItem>
              <SelectItem value="pending">{t('status.pending', 'Pendiente')}</SelectItem>
              <SelectItem value="not_required">{t('status.not_required', 'No requerida')}</SelectItem>
              <SelectItem value="invoiced">{t('status.invoiced', 'Facturada')}</SelectItem>
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label={t('filters.invoice', 'Factura')}>
          <DynamicCombobox
            value={filters.invoice_id}
            setValue={(value) => onChange({ invoice_id: value })}
            placeholder={t('filters.invoicePlaceholder', 'Seleccionar factura')}
            searchFn={searchInvoices}
            resolveSelectedOption={resolveInvoiceOption}
            dataTestId="filter-invoice"
            emptyMessage={t('filters.noInvoices', 'No se encontraron facturas')}
          />
        </FilterField>
      </div>

      {activeFiltersCount > 0 && (
        <div className="flex justify-end border-t border-card-border pt-3">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={onClear}
            type="button"
            data-testid="minimum-fees-clear-filters"
          >
            {t('filters.clear', 'Limpiar filtros')} ({activeFiltersCount})
          </Button>
        </div>
      )}
    </div>
  );
};
